import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { ingredientService } from '../../services/ingredientService';
import { IngredientFamily, CreateIngredientFamilyRequest, UpdateIngredientFamilyRequest } from '../../types/ingredient';

interface Props {
  family?: IngredientFamily | null;
  onClose: () => void;
  onSuccess: () => void;
}

const IngredientFamilyModal: React.FC<Props> = ({ family, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!family;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateIngredientFamilyRequest>({
    defaultValues: {
      name: family?.name || '',
      description: family?.description || '',
      status: family?.status || 'ACTIVE',
    }
  });

  useEffect(() => {
    if (family) {
      reset({ name: family.name, description: family.description || '', status: family.status });
    }
  }, [family, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateIngredientFamilyRequest) => ingredientService.createIngredientFamily(data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create ingredient family'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateIngredientFamilyRequest) => ingredientService.updateIngredientFamily(family!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to update ingredient family'),
  });

  const onSubmit = (data: CreateIngredientFamilyRequest) => {
    setError(null);
    isEditing ? updateMutation.mutate(data) : createMutation.mutate(data);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Ingredient Family' : 'Create Ingredient Family'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="form-label">Name *</label>
                      <input {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter family name" />
                      {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Description</label>
                      <textarea {...register('description')} className="form-input" rows={3} placeholder="Optional description" />
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <select {...register('status')} className="form-input">
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" loading={isLoading}>{isEditing ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default IngredientFamilyModal;

